import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Button } from '@cmsgov/ds-healthcare-gov';
import { formatDate, getDateString } from '../../../utilities/date';
import { formatCurrency } from '../../../utilities/currency';
import { frequencyMappedToDisplay, FrequencyType } from '../../../common/enums/FrequencyTypes';
import { incomeLabel, IncomeType } from '../../../common/enums/IncomeTypes';
import { DateObject } from '../IncomeFields/IncomeFields';

type Props = {
  incomeType: IncomeType;
  incomeTypeIndex?: number | null;
  incomeFrequency: FrequencyType;
  amount: string;
  name?: string | null;
  startDate?: DateObject | null;
  endDate?: DateObject | null;
  calculatedTotal: number;
  onRemoveIncome: () => void;
  onEditClick: () => void;
};

export const getIncomeSummaryString = (props: Omit<Props, 'onRemoveIncome' | 'onEditClick'> & { t: TFunction }) => {
  const { incomeTypeIndex, incomeType, name, amount, t, incomeFrequency, startDate, endDate } = props;
  const hasDates =
    startDate && endDate && Object.values(startDate).every((v) => v) && Object.values(endDate).every((v) => v);
  const label = hasDates ? 'incomeSummaryRowDates' : 'incomeSummaryRow';

  return t(label, {
    incomeSource: name || `${t(incomeLabel[incomeType])} `,
    autoNumber: name ? '' : incomeTypeIndex,
    amount: formatCurrency(parseFloat(amount)),
    frequency: t(frequencyMappedToDisplay[incomeFrequency]).toLowerCase(),
    startDate: hasDates ? formatDate(getDateString(startDate) ?? '', 'MM/DD/YYYY') || '' : '',
    endDate: hasDates ? formatDate(getDateString(endDate) ?? '', 'MM/DD/YYYY') || '' : '',
  });
};

const IncomeSummary = (props: Props) => {
  const { t } = useTranslation();
  const { incomeTypeIndex, incomeType } = props;

  const ariaLabel = (buttonLabel: string) => `${t(buttonLabel)} ${t(incomeLabel[incomeType])} ${incomeTypeIndex ?? ''}`;

  let incomeSummary = getIncomeSummaryString({ ...props, t });
  if (incomeSummary) {
    const index = incomeSummary.indexOf(':') + 1;

    const header = incomeSummary.substring(0, index);
    const info = incomeSummary.substring(index);

    incomeSummary = `<strong>${header}</strong>${info}`;
  }

  return (
    <Fragment>
      <div className="ds-l-col">
        <div className="ds-u-display--flex ds-u-justify-content--between ds-u-margin-y--1">
          <div
            className="ds-l-col--7 ds-l-md-col--9 ds-u-padding--0 ds-u-word-break"
            dangerouslySetInnerHTML={{ __html: incomeSummary }}
          />
          <div className="ds-l-col--3 ds-u-padding--0 ds-u-text-align--right">
            {formatCurrency(props.calculatedTotal)}
          </div>
        </div>
      </div>
      <div className="ds-l-col--12 ds-u-margin-bottom--2">
        <Button
          className="ds-u-margin-right--1"
          size="small"
          onClick={props.onEditClick}
          aria-label={ariaLabel('edit')}
        >
          {t('edit')}
        </Button>
        <Button size="small" onClick={props.onRemoveIncome} aria-label={ariaLabel('remove')}>
          {t('remove')}
        </Button>
        <div className="ds-u-border-bottom--1 ds-u-padding-bottom--2" />
      </div>
    </Fragment>
  );
};

export default IncomeSummary;
